import { useTranslation } from "@sagot/i18n";
import { Badge, Button, EmptyState, ErrorState, Icon, Skeleton, cn } from "@sagot/ui";
import { useState } from "react";
import { isPlanGateError, UpgradeCard } from "../shared/plan-gate";
import { formatNumber } from "../shared/format";
import type { AudienceOut, MatrixSource } from "../shared/types";
import { useAudiences } from "../shared/use-shared-queries";
import { audienceIcon, isVerificationMethod, METHOD_META } from "./audience-meta";
import { AudienceEditDialog } from "./audience-edit-dialog";
import { accessSummary } from "./matrix-model";
import { useAccessMatrix, useAudienceAgents, useCanManageAudiences } from "./use-audiences-data";

const MAX_CHIPS = 6;

function VerifiedBy({ method }: { method: string | null }) {
  const { t } = useTranslation();
  if (!isVerificationMethod(method)) return <Badge>{t("deployKnowledge.audiences.methodUnset")}</Badge>;
  const meta = METHOD_META[method];
  return (
    <Badge tone={meta.tone}>
      <Icon name={meta.icon} className="size-3" />
      {t(`deployKnowledge.audiences.methods.${method}`)}
    </Badge>
  );
}

function AccessChips({ code, sources }: { code: number; sources: readonly MatrixSource[] }) {
  const { t } = useTranslation();
  const summary = accessSummary(code, sources);
  if (summary.all) return <Badge>{t("deployKnowledge.audiences.allSources", { count: summary.total })}</Badge>;
  if (!summary.everythingPublic && summary.titles.length === 0) return <span className="text-xs text-fg-subtle">{t("deployKnowledge.audiences.noSources")}</span>;
  const shown = summary.titles.slice(0, MAX_CHIPS);
  const more = summary.titles.length - shown.length;
  return (
    <>
      {summary.everythingPublic ? <Badge>{t("deployKnowledge.audiences.everythingPublic")}</Badge> : null}
      {shown.map((title) => (
        <Badge key={title} tone="brand" className="max-w-[16rem] truncate">
          {title}
        </Badge>
      ))}
      {more > 0 ? <Badge>{t("deployKnowledge.audiences.moreSources", { count: more })}</Badge> : null}
    </>
  );
}

interface CardProps {
  audience: AudienceOut;
  sources: readonly MatrixSource[];
  agentNames: string[];
  canManage: boolean;
  onEdit: () => void;
  onEditAccess: () => void;
}

function AudienceCard({ audience, sources, agentNames, canManage, onEdit, onEditAccess }: CardProps) {
  const { t, i18n } = useTranslation();
  const icon = audienceIcon(audience.code, audience.icon);
  return (
    <article className="card flex flex-col" aria-labelledby={`aud-${String(audience.code)}`}>
      <div className="card-body flex-1">
        <div className="flex items-start gap-3">
          <span className={cn("inline-flex size-10 shrink-0 items-center justify-center rounded-lg", icon.box)}>
            <Icon name={icon.icon} className="size-5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 id={`aud-${String(audience.code)}`} className="text-base font-bold">
                {audience.label}
              </h2>
              {audience.is_default ? <Badge>{t("deployKnowledge.audiences.default")}</Badge> : null}
            </div>
            <p className="text-xs text-fg-subtle">{audience.description ?? t("deployKnowledge.audiences.noDescription")}</p>
          </div>
          <div className="text-right">
            <div className="text-lg font-extrabold tabular-nums">{formatNumber(audience.stats?.end_users ?? 0, i18n.language)}</div>
            <div className="text-[11px] text-fg-subtle">{t("deployKnowledge.audiences.endUsersUnit")}</div>
          </div>
        </div>
        <dl className="mt-4 space-y-2 text-sm">
          <div className="flex gap-3">
            <dt className="w-24 shrink-0 text-xs font-semibold text-fg-subtle">{t("deployKnowledge.audiences.verifiedBy")}</dt>
            <dd>
              <VerifiedBy method={audience.verification_method} />
            </dd>
          </div>
          <div className="flex gap-3">
            <dt className="w-24 shrink-0 text-xs font-semibold text-fg-subtle">{t("deployKnowledge.audiences.canAccess")}</dt>
            <dd className="flex min-w-0 flex-wrap gap-1.5">
              <AccessChips code={audience.code} sources={sources} />
            </dd>
          </div>
        </dl>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line px-5 py-3">
        <span className="min-w-0 text-xs text-fg-subtle">
          {agentNames.length > 0 ? t("deployKnowledge.audiences.agentsLine", { agents: agentNames.join(", ") }) : t("deployKnowledge.audiences.noAgents")}
        </span>
        <div className="flex gap-1">
          {canManage ? (
            <Button variant="ghost" size="sm" onClick={onEdit} aria-label={t("deployKnowledge.audiences.editNamed", { name: audience.label })}>
              <Icon name="pencil" />
              {t("deployKnowledge.audiences.edit")}
            </Button>
          ) : null}
          <Button variant="ghost" size="sm" onClick={onEditAccess}>
            <Icon name="sliders-horizontal" />
            {canManage ? t("deployKnowledge.audiences.editAccess") : t("deployKnowledge.audiences.viewAccess")}
          </Button>
        </div>
      </div>
    </article>
  );
}

function CardsSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2" aria-hidden="true">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="card card-body space-y-4">
          <div className="flex gap-3">
            <Skeleton className="size-10" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-[160px]" />
              <Skeleton className="h-3 w-[220px]" />
            </div>
          </div>
          <Skeleton className="h-4 w-[260px]" />
          <Skeleton className="h-6 w-full" />
        </div>
      ))}
    </div>
  );
}

/** Audiences tab: one card per fixed audience code with tenant labels. */
export function AudiencesPanel({ onEditAccess }: { onEditAccess: () => void }) {
  const { t } = useTranslation();
  const audiences = useAudiences();
  const matrix = useAccessMatrix();
  const agents = useAudienceAgents();
  const canManage = useCanManageAudiences();
  const [editing, setEditing] = useState<AudienceOut | null>(null);
  if (audiences.isPending) return <CardsSkeleton />;
  if (audiences.isError) {
    if (isPlanGateError(audiences.error)) return <UpgradeCard feature={t("deployKnowledge.audiences.title")} />;
    return (
      <div className="card">
        <ErrorState title={t("deployKnowledge.loadError")} description={t("deployKnowledge.loadErrorBody")} onRetry={() => void audiences.refetch()} retryLabel={t("common.retry")} />
      </div>
    );
  }
  if (audiences.data.length === 0) {
    return (
      <div className="card">
        <EmptyState icon="users" title={t("deployKnowledge.audiences.emptyTitle")} description={t("deployKnowledge.audiences.emptyBody")} />
      </div>
    );
  }
  const sources = matrix.data?.sources ?? [];
  return (
    <>
      <section className="grid grid-cols-1 gap-4 md:grid-cols-2" aria-label={t("deployKnowledge.audiences.tabAudiences")}>
        {audiences.data.map((audience) => (
          <AudienceCard
            key={audience.code}
            audience={audience}
            sources={sources}
            agentNames={(agents.data ?? []).filter((a) => a.audiences.includes(audience.code)).map((a) => a.name)}
            canManage={canManage}
            onEdit={() => { setEditing(audience); }}
            onEditAccess={onEditAccess}
          />
        ))}
      </section>
      {editing ? <AudienceEditDialog audience={editing} onClose={() => { setEditing(null); }} /> : null}
    </>
  );
}
